import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { RefreshCw, Mail, CheckCircle2, Eye, MousePointerClick, AlertTriangle, XCircle, Clock, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import EmailPreviewDialog from "./EmailPreviewDialog";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_META = {
  pending: { label: "Pending", color: "bg-slate-100 text-slate-600 border-slate-200", Icon: Clock },
  mocked: { label: "Mocked", color: "bg-slate-100 text-slate-600 border-slate-200", Icon: Clock },
  sent: { label: "Sent", color: "bg-sky-50 text-sky-700 border-sky-200", Icon: Mail },
  delivery_delayed: { label: "Delayed", color: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock },
  delivered: { label: "Delivered", color: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  opened: { label: "Opened", color: "bg-indigo-50 text-indigo-700 border-indigo-200", Icon: Eye },
  clicked: { label: "Clicked", color: "bg-violet-50 text-violet-700 border-violet-200", Icon: MousePointerClick },
  bounced: { label: "Bounced", color: "bg-rose-50 text-rose-700 border-rose-200", Icon: AlertTriangle },
  complained: { label: "Complained", color: "bg-rose-50 text-rose-700 border-rose-200", Icon: AlertTriangle },
  failed: { label: "Failed", color: "bg-rose-50 text-rose-700 border-rose-200", Icon: XCircle },
};

const CATEGORY_TABS = [
  { value: "all", label: "All" },
  { value: "automation", label: "Automations" },
  { value: "quote", label: "Quotes" },
  { value: "invoice", label: "Invoices" },
  { value: "review", label: "Reviews" },
  { value: "referral", label: "Referrals" },
  { value: "event", label: "Events" },
  { value: "team", label: "Team Alerts" },
];

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.sent;
  const Icon = meta.Icon;
  return (
    <Badge variant="outline" className={`${meta.color} text-[11px] font-semibold flex items-center gap-1 border`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </Badge>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

export default function ProjectEmails({ projectId }) {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [emails, setEmails] = useState([]);
  const [summary, setSummary] = useState({ total: 0, sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, failed: 0 });
  const [category, setCategory] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API_URL}/projects/${projectId}/emails`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmails(data.emails || []);
      setSummary(data.summary || { total: 0, sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0, failed: 0 });
    } catch (err) {
      console.error("Failed to load project emails:", err);
      if (!silent) toast.error("Couldn't load emails for this project");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (projectId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const filtered = useMemo(() => {
    if (category === "all") return emails;
    return emails.filter((e) => e.category === category);
  }, [emails, category]);

  const openRate = summary.sent > 0 ? Math.round((summary.opened / summary.sent) * 100) : 0;

  return (
    <Card data-testid="project-emails-tab">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Emails Sent
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="refresh-emails-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary strip */}
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2" data-testid="emails-summary-strip">
          <SummaryPill label="Total" value={summary.total} tint="bg-slate-50 text-slate-700 border-slate-200" />
          <SummaryPill label="Sent" value={summary.sent} tint="bg-sky-50 text-sky-700 border-sky-200" />
          <SummaryPill label="Delivered" value={summary.delivered} tint="bg-emerald-50 text-emerald-700 border-emerald-200" />
          <SummaryPill label="Opened" value={summary.opened} tint="bg-indigo-50 text-indigo-700 border-indigo-200" sub={summary.sent ? `${openRate}%` : ""} />
          <SummaryPill label="Clicked" value={summary.clicked} tint="bg-violet-50 text-violet-700 border-violet-200" />
          <SummaryPill label="Bounced" value={summary.bounced + summary.failed} tint="bg-rose-50 text-rose-700 border-rose-200" />
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-1.5" data-testid="emails-category-filter">
          {CATEGORY_TABS.map((tab) => {
            const count = tab.value === "all" ? emails.length : emails.filter((e) => e.category === tab.value).length;
            if (tab.value !== "all" && count === 0) return null;
            const active = category === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => setCategory(tab.value)}
                data-testid={`emails-cat-${tab.value}`}
                className={`text-xs px-3 py-1.5 rounded-full font-medium border transition ${
                  active
                    ? "bg-[#0F4C81] text-white border-[#0F4C81]"
                    : "bg-white text-slate-600 border-slate-200 hover:border-[#00A3E0]/60"
                }`}
              >
                {tab.label} · {count}
              </button>
            );
          })}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-10 text-sm text-slate-400" data-testid="emails-loading">Loading email history…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg" data-testid="emails-empty">
            <Mail className="w-6 h-6 mx-auto mb-2 text-slate-300" />
            {category === "all"
              ? "No emails sent for this project yet."
              : "No emails in this category yet."}
          </div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden" data-testid="emails-list">
            {filtered.map((e) => {
              const isOpen = expandedId === e.id;
              return (
                <div key={e.id} className="bg-white">
                  <div className="flex items-stretch">
                    <button
                      onClick={() => setPreviewId(e.id)}
                      className="flex-1 text-left px-3.5 py-3 hover:bg-slate-50 transition min-w-0"
                      data-testid={`email-row-${e.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <StatusBadge status={e.status} />
                          <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-200 bg-slate-50">
                            {e.category_label}
                          </Badge>
                          <span className="text-[11px] text-slate-400">{fmtDate(e.created_at)}</span>
                        </div>
                        <p className="text-sm font-medium text-slate-800 mt-1.5 truncate">{e.subject || "(no subject)"}</p>
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          To: <span className="font-medium">{e.to}</span>
                        </p>
                      </div>
                    </button>
                    <button
                      onClick={() => setExpandedId(isOpen ? null : e.id)}
                      className="px-3 flex items-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 border-l border-slate-100 transition"
                      data-testid={`email-toggle-${e.id}`}
                      title={isOpen ? "Hide details" : "Show details"}
                      aria-label="Toggle details"
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                  {isOpen && (
                    <div className="px-3.5 pb-3 pt-1 bg-slate-50 border-t border-slate-100 text-xs text-slate-600 space-y-1.5" data-testid={`email-detail-${e.id}`}>
                      <div><span className="text-slate-400">From:</span> <span className="font-medium">{e.from_email || "—"}</span></div>
                      {e.reply_to && <div><span className="text-slate-400">Reply to:</span> <span className="font-medium">{e.reply_to}</span></div>}
                      {e.status_detail && <div><span className="text-slate-400">Detail:</span> <span className="font-medium">{e.status_detail}</span></div>}
                      {e.delivered_at && <div className="text-emerald-700">✔ Delivered {fmtDate(e.delivered_at)}</div>}
                      {e.opened_at && <div className="text-indigo-700">👁 Opened {fmtDate(e.opened_at)}</div>}
                      {e.clicked_at && <div className="text-violet-700">🖱 Clicked {fmtDate(e.clicked_at)}</div>}
                      {e.bounced_at && <div className="text-rose-700">⚠ Bounced {fmtDate(e.bounced_at)}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      <EmailPreviewDialog
        open={!!previewId}
        onOpenChange={(open) => !open && setPreviewId(null)}
        recordId={previewId}
      />
    </Card>
  );
}

function SummaryPill({ label, value, tint, sub }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${tint}`} data-testid={`email-summary-${label.toLowerCase()}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-bold flex items-baseline gap-1.5">
        {value}
        {sub && <span className="text-[10px] font-semibold opacity-70">({sub})</span>}
      </div>
    </div>
  );
}
